import {useState, useEffect} from 'react';
import postCreateTaskFlow from '../functions/API/postCreateTaskFlow.js'
import postUpdateTaskFlow from '../functions/API/postUpdateTaskFlow.js'
import { toast } from '../lib/toast.js';

export default function NewTaskFlowForm({setVisible, refresh, refreshValue, editMode = false, existingData = null}) {
    const [newTaskFlow, setNewTaskFlow] = useState({
        id: "",
        name: "",
        description: "",
        tenant_requestable: false,
        is_onboarding: false,
        admin_assignable: false,
        has_deadline: false,
        deadline_days: "",
        deadline_type: "calendar",
        help_url: "",
        help_text: "",
    })

    useEffect(() => {
        if (editMode && existingData) {
            setNewTaskFlow({
                id: existingData.id || "",
                name: existingData.name || "",
                description: existingData.description || "",
                tenant_requestable: existingData.tenant_requestable || false,
                is_onboarding: existingData.is_onboarding || false,
                admin_assignable: existingData.admin_assignable || false,
                has_deadline: existingData.has_deadline || false,
                deadline_days: existingData.deadline_days || "",
                deadline_type: existingData.deadline_type || "calendar",
                help_url: existingData.help_url || "",
                help_text: existingData.help_text || "",
            })
        }
    }, [editMode, existingData])

    async function createNewTaskFlow(){
        // Validate required fields
        if (!newTaskFlow.name || !newTaskFlow.description) {
            toast.error('Name and Description are required.');
            return;
        }

        // Validate deadline fields if has_deadline is true
        if (newTaskFlow.has_deadline && !newTaskFlow.deadline_days) {
            toast.error('Deadline days is required when deadline is enabled.');
            return;
        }

        const body = {
            name: newTaskFlow.name,
            description: newTaskFlow.description,
            tenant_requestable: newTaskFlow.tenant_requestable,
            is_onboarding: newTaskFlow.is_onboarding,
            admin_assignable: newTaskFlow.admin_assignable,
            has_deadline: newTaskFlow.has_deadline,
            deadline_days: newTaskFlow.has_deadline ? newTaskFlow.deadline_days : null,
            deadline_type: newTaskFlow.has_deadline ? newTaskFlow.deadline_type : null,
            help_url: newTaskFlow.help_url || null,
            help_text: newTaskFlow.help_text || null,
        }

        // Add ID for update operations
        if (editMode) {
            body.id = newTaskFlow.id;
        }

        const apiFunction = editMode ? postUpdateTaskFlow : postCreateTaskFlow;
        const successMessage = editMode ? 'Task flow updated successfully.' : 'Task flow created successfully.';

        const TaskFlowResponse = await apiFunction(body);
        if(TaskFlowResponse.response.ok){
            refresh(refreshValue+1);
            toast.success(successMessage);
            setVisible(false);
        }else{
            toast.error('Sorry an error occurred. Try again later.');
        }
    }

    function handleChange (event) {
        let statechange = event.target.name;
        let value = event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value;
        setNewTaskFlow({...newTaskFlow, [statechange]: value})
    }

    return (
        <form>
            <div className="space-y-12">
                <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-white">
                        {editMode ? 'Edit task flow' : 'Create new task flow'}
                    </h2>
                    <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-400">Task flows define process workflows for onboarding, requests, and administrative tasks</p>
                </div>

                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-3 md:col-span-1">
                    <div className="sm:col-span-3">
                        <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            Name
                        </label>
                        <div className="mt-2">
                            <input
                            required
                            type="text"
                            onChange={handleChange}
                            value={newTaskFlow.name}
                            name="name"
                            id="name"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                            />
                        </div>
                    </div>

                    <div className="sm:col-span-3">
                        <label htmlFor="description" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            Description
                        </label>
                        <div className="mt-2">
                            <textarea
                            required
                            onChange={handleChange}
                            value={newTaskFlow.description}
                            name="description"
                            id="description"
                            rows="3"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                            />
                        </div>
                    </div>

                    <div className="sm:col-span-3">
                        <div className="space-y-4">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    onChange={handleChange}
                                    name="tenant_requestable"
                                    id="tenant_requestable"
                                    checked={newTaskFlow.tenant_requestable}
                                    className="h-4 w-4 rounded border-gray-300 dark:border-zinc-600 text-blue-700 focus:ring-blue-700"
                                />
                                <label htmlFor="tenant_requestable" className="ml-3 block text-sm font-medium text-gray-900 dark:text-white">
                                    Tenant Requestable
                                </label>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    onChange={handleChange}
                                    name="is_onboarding"
                                    id="is_onboarding"
                                    checked={newTaskFlow.is_onboarding}
                                    className="h-4 w-4 rounded border-gray-300 dark:border-zinc-600 text-blue-700 focus:ring-blue-700"
                                />
                                <label htmlFor="is_onboarding" className="ml-3 block text-sm font-medium text-gray-900 dark:text-white">
                                    Is Onboarding
                                </label>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    onChange={handleChange}
                                    name="admin_assignable"
                                    id="admin_assignable"
                                    checked={newTaskFlow.admin_assignable}
                                    className="h-4 w-4 rounded border-gray-300 dark:border-zinc-600 text-blue-700 focus:ring-blue-700"
                                />
                                <label htmlFor="admin_assignable" className="ml-3 block text-sm font-medium text-gray-900 dark:text-white">
                                    Admin Assignable
                                </label>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    onChange={handleChange}
                                    name="has_deadline"
                                    id="has_deadline"
                                    checked={newTaskFlow.has_deadline}
                                    className="h-4 w-4 rounded border-gray-300 dark:border-zinc-600 text-blue-700 focus:ring-blue-700"
                                />
                                <label htmlFor="has_deadline" className="ml-3 block text-sm font-medium text-gray-900 dark:text-white">
                                    Has Deadline
                                </label>
                            </div>
                        </div>
                    </div>

                    {newTaskFlow.has_deadline && (
                        <>
                            <div className="sm:col-span-3">
                                <label htmlFor="deadline_days" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                                    Deadline Days
                                </label>
                                <div className="mt-2">
                                    <input
                                        required={newTaskFlow.has_deadline}
                                        type="number"
                                        onChange={handleChange}
                                        value={newTaskFlow.deadline_days}
                                        name="deadline_days"
                                        id="deadline_days"
                                        min="1"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                                    />
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label htmlFor="deadline_type" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                                    Deadline Type
                                </label>
                                <div className="mt-2">
                                    <select
                                        required={newTaskFlow.has_deadline}
                                        onChange={handleChange}
                                        name="deadline_type"
                                        id="deadline_type"
                                        value={newTaskFlow.deadline_type}
                                        className="pl-2 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                                    >
                                        <option value="calendar">Calendar Days</option>
                                        <option value="working">Working Days</option>
                                    </select>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="sm:col-span-3">
                        <label htmlFor="help_url" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            Help URL (optional)
                        </label>
                        <div className="mt-2">
                            <input
                                type="url"
                                onChange={handleChange}
                                value={newTaskFlow.help_url}
                                name="help_url"
                                id="help_url"
                                placeholder="https://..."
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                            />
                        </div>
                    </div>

                    <div className="sm:col-span-3">
                        <label htmlFor="help_text" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            Help Text (optional)
                        </label>
                        <div className="mt-2">
                            <textarea
                                onChange={handleChange}
                                value={newTaskFlow.help_text}
                                name="help_text"
                                id="help_text"
                                rows="3"
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                            />
                        </div>
                    </div>

                </div>
            </div>

            <div className="mt-6 flex items-center justify-start gap-x-6">
                <button
                type="button"
                onClick={()=>{createNewTaskFlow()}}
                className="rounded-md bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
                >
                {editMode ? 'Update' : 'Create'}
                </button>
                <button type="button" onClick={()=>{setVisible(false)}} className="text-sm font-semibold leading-6 text-gray-900 dark:text-white">
                Cancel
                </button>
            </div>
        </form>
    );
}
